<template>
  <div class="data-deletion-page">
    <div class="container">
      <div class="deletion-card">
        <div class="card-header">
          <div class="icon">
            <i class="fas fa-shield-alt"></i>
          </div>
          <h1>Data Deletion Request</h1>
          <h2>Заявка за изтриване на данни</h2>
        </div>
        
        <div class="card-body">
          <!-- Language tabs removed for BG-only version -->
          <!--
          <div class="language-tabs">
            <button 
              @click="currentLanguage = 'en'" 
              :class="['tab-btn', { active: currentLanguage === 'en' }]"
            >
              English
            </button>
            <button 
              @click="currentLanguage = 'bg'" 
              :class="['tab-btn', { active: currentLanguage === 'bg' }]"
            >
              Български
            </button>
          </div>
          -->

          <!-- English Content commented out for BG-only version -->
          <!--
          <div v-if="currentLanguage === 'en'" class="content-section">
            ...ENGLISH UI...
          </div>
          -->

          <!-- Bulgarian Content -->
          <div v-if="currentLanguage === 'bg'" class="content-section">
            <div class="info-section">
              <h3>Заявка за изтриване на акаунт и данни</h3>
              <p>
                Ако искате да изтриете вашия акаунт и всички свързани данни от приложението NovJivot, 
                моля попълнете формата по-долу. Това действие е окончателно и не може да бъде отменено.
                <strong> Профилът ви ще бъде изтрит незабавно след потвърждение.</strong>
              </p>
              
              <div class="data-info">
                <h4>Какви данни ще бъдат изтрити:</h4>
                <ul>
                  <li>Вашият потребителски акаунт и профилна информация</li>
                  <li>Данни за принадлежност към църква</li>
                  <li>Предпочитания и настройки на приложението</li>
                  <li>Всички други лични данни, свързани с вашия акаунт</li>
                </ul>
              </div>

              <div class="timeline-info">
                <h4>Процес на изтриване:</h4>
                <p>
                  След потвърждение на вашата парола, профилът ви ще бъде <strong>изтрит незабавно</strong>. 
                  Всички данни ще бъдат окончателно премахнати от системата веднага.
                </p>
              </div>
            </div>

            <form @submit.prevent="submitRequest" class="deletion-form">
              <div class="form-group">
                <label for="email-bg" class="form-label">Имейл адрес *</label>
                <input
                  id="email-bg"
                  type="email"
                  v-model="form.email"
                  @blur="checkEmailEntered"
                  class="form-control"
                  placeholder="Въведете вашия регистриран имейл адрес"
                  required
                />
                <small class="form-text">
                  Моля въведете имейл адреса, свързан с вашия NovJivot акаунт
                </small>
              </div>

              <!-- Password field appears after email is entered -->
              <div v-if="showPasswordField" class="form-group">
                <label for="password-bg" class="form-label">Парола *</label>
                <input
                  id="password-bg"
                  type="password"
                  v-model="form.password"
                  class="form-control"
                  placeholder="Въведете вашата парола за потвърждение"
                  required
                />
                <small class="form-text">
                  Въведете паролата си за потвърждение на изтриването
                </small>
              </div>

              <div v-if="showPasswordField" class="form-group">
                <label for="reason-bg" class="form-label">Причина за изтриване (по избор)</label>
                <textarea
                  id="reason-bg"
                  v-model="form.reason"
                  class="form-control"
                  rows="4"
                  placeholder="Моля кажете ни защо изтривате акаунта си (по избор)"
                ></textarea>
              </div>

              <div v-if="showPasswordField" class="form-group">
                <label class="checkbox-container">
                  <input
                    type="checkbox"
                    v-model="form.confirmDeletion"
                    required
                  />
                  <span class="checkmark"></span>
                  <span class="checkbox-text">
                    Разбирам, че това действие е окончателно и не може да бъде отменено.
                    Всички мои данни ще бъдат окончателно изтрити.
                  </span>
                </label>
              </div>

              <div class="form-actions">
                <button
                  v-if="!showPasswordField"
                  type="button"
                  @click="proceedToPassword"
                  class="btn btn-primary"
                  :disabled="!form.email"
                >
                  <i class="fas fa-arrow-right"></i>
                  Продължи
                </button>

                <button
                  v-if="showPasswordField"
                  type="button"
                  @click="showDeletionConfirmation"
                  class="btn btn-danger"
                  :disabled="submitting || !form.confirmDeletion || !form.password"
                >
                  <i class="fas fa-exclamation-triangle"></i>
                  Изтрий профила
                </button>
              </div>
            </form>
          </div>

          <!-- Success Message (BG only) -->
          <div v-if="showSuccess" class="success-message">
            <div class="success-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h3>Профилът е изтрит успешно</h3>
            <p>
              Вашият профил и свързаните с него лични данни бяха успешно изтрити от системата.
              Всички ваши профилни данни и настройки са окончателно премахнати.
            </p>
          </div>
        </div>

        <div class="card-footer">
          <div class="contact-info">
            <p>
              <strong>Нужна е помощ?</strong> Свържете се с нас на 
              <a href="mailto:support@novjivot.com">support@novjivot.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Dialog Modal -->
    <div v-if="showConfirmationDialog" class="modal-overlay">
      <div class="confirmation-modal">
        <div class="modal-header">
          <h3>⚠️ Потвърждение за изтриване</h3>
        </div>
        <div class="modal-body">
          <p><strong>Сигурни ли сте, че искате да изтриете профила си?</strong></p>
          <div class="warning-box">
            <p>🚨 <strong>ВНИМАНИЕ: Това действие е необратимо!</strong></p>
            <ul>
              <li>Всички ваши профилни данни ще бъдат окончателно изтрити</li>
              <li>Няма да можете да възстановите акаунта си</li>
              <li>Всички настройки и предпочитания ще бъдат загубени</li>
              <li>Процесът не може да бъде отменен</li>
            </ul>
          </div>
          <p>Ако сте сигурни, че искате да продължите, натиснете "Потвърди изтриването".</p>
        </div>
        <div class="modal-footer">
          <button @click="cancelDeletion" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Отказ
          </button>
          <button @click="confirmAndDelete" class="btn btn-danger" :disabled="submitting">
            <i class="fas fa-trash" v-if="!submitting"></i>
            <i class="fas fa-spinner fa-spin" v-if="submitting"></i>
            {{ submitting ? 'Изтриване...' : 'Потвърди изтриването' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth'

export default {
  name: 'DataDeletionRequest',
  setup() {
    const auth = getAuth()
    
    const currentLanguage = ref('bg')
    const submitting = ref(false)
    const showSuccess = ref(false)
    const showPasswordField = ref(false)
    const isAuthenticated = ref(false)
    const showConfirmationDialog = ref(false)
    const form = ref({
      email: '',
      password: '',
      reason: '',
      confirmDeletion: false
    })

    let unsubscribeAuth = null

    // Check authentication status
    onMounted(() => {
      unsubscribeAuth = onAuthStateChanged(auth, (user) => {
        isAuthenticated.value = !!user
        if (user) {
          form.value.email = user.email
        }
      })
    })

    onUnmounted(() => {
      if (unsubscribeAuth) {
        unsubscribeAuth()
      }
    })

    const checkEmailEntered = () => {
      if (form.value.email && form.value.email.includes('@')) {
        showPasswordField.value = true
      }
    }

    const proceedToPassword = () => {
      if (form.value.email && form.value.email.includes('@')) {
        showPasswordField.value = true
      }
    }

    const showDeletionConfirmation = () => {
      showConfirmationDialog.value = true
    }

    const cancelDeletion = () => {
      showConfirmationDialog.value = false
    }

    const confirmAndDelete = async () => {
      showConfirmationDialog.value = false
      await submitRequest()
    }

    const submitRequest = async () => {
      if (!form.value.email || !form.value.password || !form.value.confirmDeletion) {
        return
      }

      submitting.value = true

      try {
        // First authenticate the user
        if (!isAuthenticated.value) {
          await signInWithEmailAndPassword(auth, form.value.email, form.value.password)
        }

        // Call the Firebase function for direct profile deletion
        const response = await fetch('https://europe-west1-novjivot-church.cloudfunctions.net/deleteProfile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: form.value.email.toLowerCase().trim(),
            password: form.value.password
          })
        })

        const result = await response.json()

        if (!response.ok) {
          throw new Error(result.error || 'Failed to delete profile')
        }

        console.log('Profile deletion result:', result)

        // Show success message
        showSuccess.value = true
        resetForm()
        window.scrollTo({ top: 0, behavior: 'smooth' })

      } catch (error) {
        console.error('Error deleting profile:', error)

        // Show specific error messages
        if (error.message.includes('Invalid credentials') || error.message.includes('User not found')) {
          alert('Невалиден имейл или парола. Моля проверете данните си и опитайте отново.')
        } else {
          alert('Възникна грешка при изтриването на профила. Моля опитайте отново или се свържете с поддръжката.')
        }
      } finally {
        submitting.value = false
      }
    }

    const resetForm = () => {
      form.value = {
        email: '',
        password: '',
        reason: '',
        confirmDeletion: false
      }
      showPasswordField.value = false
    }

    return {
      currentLanguage,
      submitting,
      showSuccess,
      showPasswordField,
      isAuthenticated,
      showConfirmationDialog,
      form,
      checkEmailEntered,
      proceedToPassword,
      showDeletionConfirmation,
      cancelDeletion,
      confirmAndDelete,
      submitRequest,
      resetForm
    }
  }
}
</script>

<style scoped>
.data-deletion-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.container {
  width: 100%;
  max-width: 800px;
}

.deletion-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #1D687B 0%, #2980b9 100%);
  color: white;
  padding: 40px 30px;
  text-align: center;
}

.card-header .icon {
  font-size: 48px;
  margin-bottom: 20px;
  opacity: 0.9;
}

.card-header h1 {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.card-header h2 {
  font-size: 20px;
  font-weight: 400;
  margin: 0;
  opacity: 0.9;
}

.card-body {
  padding: 40px 30px;
}

.language-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 30px;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 4px;
}

.tab-btn {
  flex: 1;
  padding: 12px 20px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  color: #64748b;
}

.tab-btn.active {
  background: white;
  color: #1D687B;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tab-btn:hover:not(.active) {
  color: #475569;
}

.content-section h3 {
  color: #1e293b;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
}

.info-section {
  margin-bottom: 30px;
}

.info-section p {
  color: #475569;
  line-height: 1.6;
  margin-bottom: 24px;
}

.data-info, .timeline-info {
  background: #f8fafc;
  border-left: 4px solid #1D687B;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;
}

.data-info h4, .timeline-info h4 {
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.data-info ul {
  margin: 0;
  padding-left: 20px;
}

.data-info li {
  color: #475569;
  margin-bottom: 8px;
  line-height: 1.5;
}

.timeline-info p {
  margin: 0;
  color: #475569;
  line-height: 1.6;
}

.deletion-form {
  background: #fafbfc;
  padding: 30px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #1D687B;
  box-shadow: 0 0 0 3px rgba(29, 104, 123, 0.1);
}

.form-text {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

.checkbox-container {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  position: relative;
  padding-left: 35px;
  margin: 0;
}

.checkbox-container input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 2px;
  left: 0;
  height: 20px;
  width: 20px;
  background-color: white;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.checkbox-container:hover input ~ .checkmark {
  border-color: #1D687B;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #1D687B;
  border-color: #1D687B;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-container .checkmark:after {
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-text {
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
}

.form-actions {
  text-align: center;
  margin-top: 30px;
}

.btn {
  padding: 14px 32px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  min-width: 200px;
  justify-content: center;
}

.btn-danger {
  background: #dc2626;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.success-message {
  text-align: center;
  padding: 40px 20px;
  background: #f0fdf4;
  border: 2px solid #bbf7d0;
  border-radius: 12px;
  margin-top: 20px;
}

.success-icon {
  font-size: 48px;
  color: #16a34a;
  margin-bottom: 20px;
}

.success-message h3 {
  color: #15803d;
  font-size: 24px;
  margin-bottom: 12px;
}

.success-message p {
  color: #166534;
  line-height: 1.6;
  margin: 0;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.confirmation-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  padding: 24px 24px 0;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0 0 16px 0;
  color: #dc2626;
  font-size: 20px;
  font-weight: 600;
}

.modal-body {
  padding: 24px;
}

.modal-body p {
  margin-bottom: 16px;
  line-height: 1.6;
}

.warning-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
}

.warning-box p {
  color: #dc2626;
  font-weight: 600;
  margin-bottom: 12px;
}

.warning-box ul {
  color: #7f1d1d;
  margin: 0;
  padding-left: 20px;
}

.warning-box li {
  margin-bottom: 4px;
}

.modal-footer {
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #4b5563;
}

.card-footer {
  background: #f8f9fa;
  padding: 20px 30px;
  border-top: 1px solid #e9ecef;
  text-align: center;
}

.contact-info p {
  margin: 0;
  color: #6c757d;
  font-size: 14px;
}

.contact-info a {
  color: #1D687B;
  text-decoration: none;
  font-weight: 500;
}

.contact-info a:hover {
  text-decoration: underline;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .data-deletion-page {
    padding: 10px;
  }
  
  .card-header {
    padding: 30px 20px;
  }
  
  .card-header h1 {
    font-size: 24px;
  }
  
  .card-header h2 {
    font-size: 18px;
  }
  
  .card-body {
    padding: 30px 20px;
  }
  
  .deletion-form {
    padding: 20px;
  }
  
  .language-tabs {
    flex-direction: column;
  }
  
  .tab-btn {
    text-align: center;
  }
}

@media (max-width: 480px) {
  .card-header .icon {
    font-size: 36px;
  }
  
  .card-header h1 {
    font-size: 20px;
  }
  
  .card-header h2 {
    font-size: 16px;
  }
  
  .btn {
    width: 100%;
    min-width: auto;
  }
}
</style>
